import { CalendarEvent, CalendarProvider } from './event';

const LOCAL_STAMP = /^\d{8}T\d{6}$/;
const TIME_ZONE = /^[A-Za-z0-9_+/-]{1,64}$/;
const MAX_TITLE_LENGTH = 120;
const MAX_DESCRIPTION_LENGTH = 300;

export const formatLocalStamp = (date: Date): string => {
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}T${pad(date.getHours())}${pad(date.getMinutes())}${pad(date.getSeconds())}`;
};

const formatUtcStamp = (date: Date): string => {
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getUTCFullYear()}${pad(date.getUTCMonth() + 1)}${pad(date.getUTCDate())}T${pad(date.getUTCHours())}${pad(date.getUTCMinutes())}${pad(date.getUTCSeconds())}Z`;
};

export const formatUtcOffset = (date: Date, timeZone: string): string => {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).formatToParts(date);
    const lookup = Object.fromEntries(parts.map(part => [part.type, part.value]));
    const asUtc = Date.UTC(
      Number(lookup.year),
      Number(lookup.month) - 1,
      Number(lookup.day),
      Number(lookup.hour) % 24,
      Number(lookup.minute),
      Number(lookup.second),
    );
    const diffMinutes = Math.round((asUtc - date.getTime()) / 60000);
    const sign = diffMinutes >= 0 ? '+' : '-';
    const absolute = Math.abs(diffMinutes);
    const hours = String(Math.floor(absolute / 60)).padStart(2, '0');
    const minutes = String(absolute % 60).padStart(2, '0');
    return `${sign}${hours}${minutes}`;
  } catch {
    return '+0000';
  }
};

const escapeIcsText = (value: string): string =>
  value
    .replace(/\\/g, '\\\\')
    .replace(/\r\n|\r|\n/g, '\\n')
    .replace(/,/g, '\\,')
    .replace(/;/g, '\\;');

const foldLine = (line: string): string => {
  const limit = 73;
  if (line.length <= limit) return line;

  const parts = [line.slice(0, limit)];
  let rest = line.slice(limit);
  while (rest.length > 0) {
    parts.push(` ${rest.slice(0, limit - 1)}`);
    rest = rest.slice(limit - 1);
  }
  return parts.join('\r\n');
};

export interface IcsParts {
  title: string;
  description: string;
  startStamp: string;
  endStamp: string;
  timeZone: string;
  repeatCount: number;
}

interface StampParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

const parseStamp = (stamp: string): StampParts | null => {
  const match = stamp.match(/^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})$/);
  if (!match) return null;

  return {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
    hour: Number(match[4]),
    minute: Number(match[5]),
    second: Number(match[6]),
  };
};

const stampFromParts = (parts: StampParts): string => {
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${parts.year}${pad(parts.month)}${pad(parts.day)}T${pad(parts.hour)}${pad(parts.minute)}${pad(parts.second)}`;
};

const addDaysToStamp = (stamp: string, days: number): string => {
  const parts = parseStamp(stamp);
  if (!parts) return stamp;

  const shifted = new Date(
    Date.UTC(parts.year, parts.month - 1, parts.day + days, parts.hour, parts.minute, parts.second),
  );
  return stampFromParts({
    year: shifted.getUTCFullYear(),
    month: shifted.getUTCMonth() + 1,
    day: shifted.getUTCDate(),
    hour: shifted.getUTCHours(),
    minute: shifted.getUTCMinutes(),
    second: shifted.getUTCSeconds(),
  });
};

const parseOffsetMinutes = (offset: string): number => {
  const sign = offset.startsWith('-') ? -1 : 1;
  return sign * (Number(offset.slice(1, 3)) * 60 + Number(offset.slice(3, 5)));
};

const zonedStampToUtc = (stamp: string, timeZone: string): Date | null => {
  const parts = parseStamp(stamp);
  if (!parts) return null;

  const wallClock = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second,
  );
  let utc = wallClock;
  for (let attempt = 0; attempt < 3; attempt += 1) {
    utc = wallClock - parseOffsetMinutes(formatUtcOffset(new Date(utc), timeZone)) * 60_000;
  }
  return new Date(utc);
};

const formatStampInZone = (date: Date, timeZone: string): string => {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).formatToParts(date);
    const lookup = Object.fromEntries(parts.map(part => [part.type, part.value]));
    return stampFromParts({
      year: Number(lookup.year),
      month: Number(lookup.month),
      day: Number(lookup.day),
      hour: Number(lookup.hour) % 24,
      minute: Number(lookup.minute),
      second: Number(lookup.second),
    });
  } catch {
    return formatLocalStamp(date);
  }
};

/**
 * A 30-day series crosses at most one clock change. Include both offsets when it does,
 * so clients that trust this VTIMEZONE (Outlook in particular) do not keep the first
 * offset for the whole month.
 */
const buildVTimezone = (timeZone: string, startStamp: string, repeatCount: number): string[] => {
  const start = zonedStampToUtc(startStamp, timeZone) ?? new Date();
  const initial = formatUtcOffset(start, timeZone);
  const lastStamp = addDaysToStamp(startStamp, Math.max(repeatCount - 1, 0));
  const last = zonedStampToUtc(lastStamp, timeZone) ?? start;
  const finalOffset = formatUtcOffset(last, timeZone);

  const zone = (type: 'STANDARD' | 'DAYLIGHT', startAt: string, from: string, to: string) => [
    `BEGIN:${type}`,
    `DTSTART:${startAt}`,
    `TZOFFSETFROM:${from}`,
    `TZOFFSETTO:${to}`,
    `END:${type}`,
  ];

  if (initial === finalOffset) {
    return [
      'BEGIN:VTIMEZONE',
      `TZID:${timeZone}`,
      ...zone('STANDARD', '19700101T000000', initial, initial),
      'END:VTIMEZONE',
    ];
  }

  let before = start.getTime();
  let after = last.getTime();
  while (after - before > 60_000) {
    const mid = Math.floor((before + after) / 2);
    if (formatUtcOffset(new Date(mid), timeZone) === initial) before = mid;
    else after = mid;
  }

  const nextOffset = formatUtcOffset(new Date(after), timeZone);
  const initialIsDaylight = parseOffsetMinutes(initial) > parseOffsetMinutes(nextOffset);
  const following = initialIsDaylight ? 'STANDARD' : 'DAYLIGHT';
  const starting = initialIsDaylight ? 'DAYLIGHT' : 'STANDARD';

  return [
    'BEGIN:VTIMEZONE',
    `TZID:${timeZone}`,
    ...zone(starting, '19700101T000000', initial, initial),
    ...zone(following, formatStampInZone(new Date(after), timeZone), initial, nextOffset),
    'END:VTIMEZONE',
  ];
};

export const buildIcs = (parts: IcsParts, now: Date = new Date()): string => {
  const uid = `memrise-reminder-${parts.startStamp}-${parts.timeZone}@memrise.com`;
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Memrise//Calendar Reminder//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    ...buildVTimezone(parts.timeZone, parts.startStamp, parts.repeatCount),
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${formatUtcStamp(now)}`,
    `DTSTART;TZID=${parts.timeZone}:${parts.startStamp}`,
    `DTEND;TZID=${parts.timeZone}:${parts.endStamp}`,
    `RRULE:FREQ=DAILY;COUNT=${parts.repeatCount}`,
    `SUMMARY:${escapeIcsText(parts.title)}`,
    `DESCRIPTION:${escapeIcsText(parts.description)}`,
    'END:VEVENT',
    'END:VCALENDAR',
  ];

  return lines.map(foldLine).join('\r\n');
};

export const buildGoogleCalendarUrl = (event: CalendarEvent): string => {
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: event.title,
    dates: `${formatLocalStamp(event.start)}/${formatLocalStamp(event.end)}`,
    ctz: event.timeZone,
    details: event.description,
    recur: `RRULE:FREQ=DAILY;COUNT=${event.repeatCount}`,
  });

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

const asQueryString = (value: string | string[] | undefined): string | null => {
  if (typeof value === 'string') return value;
  if (Array.isArray(value) && typeof value[0] === 'string') return value[0];
  return null;
};

export const buildIcsFromQuery = (
  query: Record<string, string | string[] | undefined>,
): string | null => {
  const title = asQueryString(query.title)?.slice(0, MAX_TITLE_LENGTH);
  const description = asQueryString(query.description)?.slice(0, MAX_DESCRIPTION_LENGTH) ?? '';
  const startStamp = asQueryString(query.start);
  const endStamp = asQueryString(query.end);
  const timeZone = asQueryString(query.tz);
  const repeatCount = Number(asQueryString(query.count));

  if (!title || !startStamp || !endStamp || !timeZone) return null;
  if (!LOCAL_STAMP.test(startStamp) || !LOCAL_STAMP.test(endStamp)) return null;
  if (!TIME_ZONE.test(timeZone)) return null;
  if (!Number.isInteger(repeatCount) || repeatCount < 1 || repeatCount > 30) return null;

  return buildIcs({
    title,
    description,
    startStamp,
    endStamp,
    timeZone,
    repeatCount,
  });
};

export const buildIcsDownloadUrl = (event: CalendarEvent): string => {
  const params = new URLSearchParams({
    title: event.title,
    description: event.description,
    start: formatLocalStamp(event.start),
    end: formatLocalStamp(event.end),
    tz: event.timeZone,
    count: String(event.repeatCount),
  });

  return `/api-web/calendar-reminder?${params.toString()}`;
};

export const openProviderCalendar = (provider: CalendarProvider, event: CalendarEvent): void => {
  const url = provider === 'google' ? buildGoogleCalendarUrl(event) : buildIcsDownloadUrl(event);
  // `noopener` in the feature string makes `window.open` return null even when the tab
  // opens, which used to fall through and open the same url a second time.
  const opened = window.open(url, '_blank');
  if (opened) {
    opened.opener = null;
    return;
  }

  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.target = '_blank';
  anchor.rel = 'noopener noreferrer';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
};
